/**
 * Plain-data types. No methods, no classes — everything here is JSON-safe.
 * If a field can't survive JSON.parse(JSON.stringify(...)), it does not
 * belong on GameState.
 */

import type { Rng } from './rng.ts';

export type Direction = 'N' | 'E' | 'S' | 'W';

export type InputFrame = {
  /** Held direction (latched until the player can act on it). */
  readonly dir: Direction | null;
  /** True on the frame the smoke key is pressed. Sim treats as edge-trigger. */
  readonly smoke: boolean;
  /** True on the frame the start key is pressed (title screen / continue). */
  readonly start?: boolean;
};

export const NO_INPUT: InputFrame = { dir: null, smoke: false };

/**
 * Phase / mode of the overall game.
 *  - title:   waiting at the title screen.
 *  - playing: stage is active, player can move.
 *  - cleared: stage cleared, awaiting transition.
 *  - dying:   player just died, awaiting respawn or game-over.
 *  - gameover: 0 lives, run is over.
 */
export type GamePhase = 'title' | 'playing' | 'cleared' | 'dying' | 'gameover';

/** Discriminator for flag behavior. Most are 'yellow' (count toward stage clear).
 * The three powerup kinds are placed one each per stage and grant unique effects
 * when collected; see scoring.ts. */
export type FlagKind = 'yellow' | 'doubler' | 'fuel_bonus' | 'speed_boost';

/**
 * GameEvent is the only path from sim/ to audio/. Events accumulate during
 * a tick and are drained by io/audio.ts each render. Pure data: no callbacks.
 */
export type GameEvent =
  | { readonly kind: 'flag_pickup'; readonly score: number; readonly flagKind: FlagKind }
  | { readonly kind: 'smoke_deploy' }
  | { readonly kind: 'enemy_stun' }
  | { readonly kind: 'player_die' }
  | { readonly kind: 'extra_life' }
  | { readonly kind: 'stage_clear' }
  | { readonly kind: 'game_over' };

/**
 * A car in the world. Position is in fixed-point subpixels (see fixed.ts).
 * `dir` is the current heading; `null` means stationary.
 */
export type Car = {
  readonly x: number;
  readonly y: number;
  readonly dir: Direction | null;
};

/**
 * All tunable game numbers live here as plain data — never as bare module
 * constants — so tests can override them and so balance work is one diff.
 */
export type GameConfig = {
  /**
   * Player car speed in subpixels per tick. Non-divisors of HALF_TILE are
   * allowed; movement clamps at tile centers on overshoot (see movement.ts).
   * A non-divisor loses a few subpixels per intersection — at speed 24 the
   * effective rate is ~23.27.
   */
  readonly playerSpeed: number;
  /** Enemy car speed. Must divide HALF_TILE evenly. */
  readonly enemySpeed: number;
  /** Stage 1 fuel in ticks. 60 ticks/s, so e.g. 10800 = 180s. Later stages shrink toward `fuelMin`. */
  readonly fuelMax: number;
  /** Ticks shaved off each stage past stage 1, floored at `fuelMin`. */
  readonly fuelStageFalloff: number;
  /** Minimum fuel any stage can have, regardless of falloff. */
  readonly fuelMin: number;
  /** Fuel consumed per tick while playing. */
  readonly fuelPerTick: number;
  /** Base value of the 1st flag in a chain (and step between chain values). */
  readonly baseFlagScore: number;
  /** Points awarded per remaining second of fuel when the fuel-bonus flag is collected. */
  readonly fuelBonusPerSecond: number;
  /** Lives at start of a run. */
  readonly startingLives: number;
  /** Score at which the first extra life is awarded. */
  readonly extraLifeFirst: number;
  /** Score interval for subsequent extra lives. */
  readonly extraLifeStep: number;
  /** Initial enemy count on Phase 5's bring-up stage. Phase 6 makes this per-stage. */
  readonly enemiesAtStart: number;
  /** Stage at which enemies double their movement speed. Stages below this run at config.enemySpeed. */
  readonly enemySpeedBoostStage: number;
  /** Fuel cost per smoke deploy. */
  readonly smokeCost: number;
  /** Smoke lifetime in ticks. */
  readonly smokeTtl: number;
  /** Enemy stun duration after entering smoke. */
  readonly smokeStunDuration: number;
  /** Ticks the 'dying' phase lasts before respawn or gameover. */
  readonly dyingDuration: number;
  /** Ticks the 'cleared' phase holds before the next stage begins. */
  readonly stageTransitionDuration: number;
  /** Every Nth stage is a Challenging Stage (frozen enemies, all-flags-or-fuel-out). */
  readonly challengingStageEvery: number;
  /** How long the speed-boost powerup lasts in ticks. */
  readonly speedBoostDuration: number;
  /** Player-speed multiplier while a speed boost is active. Allowed non-divisor of HALF_TILE. */
  readonly speedBoostMultiplier: number;
  /** Fuel-consumption multiplier while a speed boost is active. */
  readonly speedBoostFuelMultiplier: number;
};

export const DEFAULT_CONFIG: GameConfig = {
  playerSpeed: 24,
  enemySpeed: 16,
  fuelMax: 60 * 180,
  fuelStageFalloff: 60 * 10,
  fuelMin: 60 * 90,
  fuelPerTick: 1,
  baseFlagScore: 100,
  fuelBonusPerSecond: 50,
  startingLives: 3,
  extraLifeFirst: 20_000,
  extraLifeStep: 50_000,
  enemiesAtStart: 2,
  enemySpeedBoostStage: 4,
  smokeCost: 60 * 5,
  smokeTtl: 60 * 3,
  smokeStunDuration: 60 * 4,
  dyingDuration: 60,
  stageTransitionDuration: 90,
  challengingStageEvery: 3,
  speedBoostDuration: 60 * 15,
  speedBoostMultiplier: 1.5,
  speedBoostFuelMultiplier: 2,
};

import type { Maze, FlagSpec } from './maze.ts';

/** Per-flag state during a stage (collected status layered on the maze's static spec). */
export type FlagState = {
  readonly tile: FlagSpec['tile'];
  readonly kind: FlagKind;
  readonly collected: boolean;
};

/**
 * Per-enemy AI flavor. Each enemy at create-time gets assigned a personality
 * from a per-stage palette (see personality.ts); the AI in ai.ts branches on
 * this field to vary the pursuit pattern.
 */
export type EnemyPersonality = 'chaser' | 'interceptor' | 'anticipator' | 'wanderer';

export type Enemy = {
  readonly id: number;
  readonly car: Car;
  /** Tick at which the stun ends (exclusive). 0 = never stunned. */
  readonly stunUntil: number;
  readonly personality: EnemyPersonality;
};

export type Smoke = {
  /** Tile the smoke is parked on. */
  readonly tile: FlagSpec['tile'];
  /** Tick at which the smoke was deployed. */
  readonly bornTick: number;
};

export type GameState = {
  readonly tick: number;
  readonly phase: GamePhase;
  readonly rng: Rng;
  readonly config: GameConfig;
  /** Original seed for this run; per-stage seeds derive as baseSeed + stage. */
  readonly baseSeed: number;
  /** 1-indexed stage number. */
  readonly stage: number;
  /** True for stages where N % challengingStageEvery === 0 (no enemy chase). */
  readonly challenging: boolean;
  readonly maze: Maze;
  readonly player: Car;
  readonly flags: readonly FlagState[];
  readonly enemies: readonly Enemy[];
  readonly smokes: readonly Smoke[];
  readonly score: number;
  /** Index in the scoring chain — the Nth flag is worth N * baseFlagScore. */
  readonly chainStep: number;
  /** Score multiplier applied to flag values. Set to 2 by the doubler flag; resets at startStage. */
  readonly scoreMultiplier: number;
  /** Tick at which the speed boost expires (0 == not active). Set by the speed_boost flag. */
  readonly speedBoostUntil: number;
  /** How many yellow flags have been collected this stage; clear at 10. */
  readonly yellowCollected: number;
  readonly fuel: number;
  readonly lives: number;
  /** Next extra-life threshold; awarded when score crosses it. */
  readonly nextExtraLifeAt: number;
  /** When phase==='dying', the tick at which respawn or game-over fires. */
  readonly respawnAt: number;
  /** When phase==='dying', what killed the player. null otherwise. Drives the HUD banner. */
  readonly deathCause: 'fuel' | 'crash' | null;
  /** When phase==='cleared', the tick at which next stage begins. */
  readonly nextStageAt: number;
  /** Tick at which the current stage began. Drives the "CHALLENGING STAGE" intro banner. */
  readonly stageStartTick: number;
  readonly events: readonly GameEvent[];
};
